'use strict';

/**
 * leaveBlackouts — which blackout periods a leave request collides with, and what each does
 * (LEAVE-CANON-001 P3a-2 / P3a-6; ONE module since Phase 9 unit 9j-1).
 *
 * A BLACKOUT PERIOD (`planner_leave_blackouts`, owned by planner-api) is a named span of civil
 * dates, at one store or chain-wide, during which planned absence is restricted. It carries one of
 * two enforcements (owner ruling R-18: absence-policy violations WARN, "a HARD blackout still
 * refuses"):
 *
 *   - `block`: a request that overlaps it is REFUSED at submit (palm answers 409
 *     `LEAVE_BLACKOUT_PERIOD`, naming the period).
 *   - `warn`: the request is accepted with a named warning. Planner's auto-approval never decides
 *     it, and the approver sees the period on the request.
 *
 * A period applies only to a leave type that OBSERVES blackouts (`leave_types.observesBlackouts`).
 * Sick, bereavement and the statute-protected types are exempt as seeded.
 *
 * 🔴 ONE rule for every reader. The server (palm-api at submit, planner's auto-approval) and the
 * request forms (palm-web, palm-native) used to run two copies: a CJS `unified-models` constant, and
 * a palm-web twin held level by a parity suite. The one DELIBERATE difference is now an option, not
 * a second implementation. An enforcement nobody knows:
 *   - THROWS on the server (`unknown: 'throw'`, the default), because reading it as a warning would
 *     let a would-be refusal through;
 *   - counts as BLOCKING in a form (`unknown: 'block'`), because a throw would take the form down
 *     over a value the server judges anyway.
 *
 * @module status-taxonomy/leaveBlackouts
 */

/** The two enforcements — mirrored by the table's CHECK. */
const LEAVE_BLACKOUT_ENFORCEMENTS = Object.freeze(['block', 'warn']);

const UNKNOWN_POSTURES = Object.freeze(['throw', 'block']);

/**
 * Split the periods a request overlaps into the ones that REFUSE it and the ones that WARN.
 *
 * @param {Object[]} overlapping - Periods already known to overlap the request.
 * @param {{observesBlackouts: boolean}} leaveType - The tenant's `leave_types` row.
 * @param {Object} [options]
 * @param {'throw'|'block'} [options.unknown='throw'] - What an enforcement outside the enum does.
 * @returns {{blocking: Object[], warning: Object[]}}
 * @throws {Error} On an unknown enforcement under the `throw` posture, or an unknown posture.
 */
function classifyBlackoutOverlap(overlapping, leaveType, { unknown = 'throw' } = {}) {
  if (!UNKNOWN_POSTURES.includes(unknown)) {
    throw new Error(
      `classifyBlackoutOverlap: unknown must be "throw" or "block", got "${unknown}"`,
    );
  }
  const verdict = { blocking: [], warning: [] };
  if (!leaveType.observesBlackouts) return verdict;
  for (const period of overlapping) {
    if (period.enforcement === 'warn') {
      verdict.warning.push(period);
    } else if (period.enforcement === 'block' || unknown === 'block') {
      verdict.blocking.push(period);
    } else {
      throw new Error(
        `LEAVE-CANON-001: unknown blackout enforcement "${period.enforcement}" on ${period.blackoutId}`,
      );
    }
  }
  return verdict;
}

/**
 * The FORM reading: of every period the form holds, which the dates collide with, and what each
 * means. The overlap is the inclusive arithmetic `LeaveBlackoutModel.findOverlapping` does in SQL.
 *
 * @param {Object} params
 * @param {Object[]|null} params.blackouts - The store's upcoming periods, or null while loading.
 * @param {{observesBlackouts?: boolean}|undefined} params.leaveType - The type the form shows.
 * @param {string} params.startDate - `YYYY-MM-DD`, or empty while the form is incomplete.
 * @param {string} params.endDate - `YYYY-MM-DD`, or empty.
 * @returns {{blocking: Object[], warning: Object[]}} Both empty when the form cannot be judged yet.
 */
function judgeBlackoutOverlap({ blackouts, leaveType, startDate, endDate }) {
  if (!leaveType) return { blocking: [], warning: [] };
  // Nothing to judge until the range is complete and the right way round.
  if (!startDate || !endDate || endDate < startDate) return { blocking: [], warning: [] };
  const overlapping = (blackouts || []).filter(
    (period) => period.startDate <= endDate && period.endDate >= startDate,
  );
  return classifyBlackoutOverlap(overlapping, leaveType, { unknown: 'block' });
}

module.exports = { LEAVE_BLACKOUT_ENFORCEMENTS, classifyBlackoutOverlap, judgeBlackoutOverlap };
